/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-22
 */

// Add third-party dependencies.
import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  ShoppingCart as ShoppingCartIcon,
  Computer as ComputerIcon,
  Store as StoreIcon,
  ViewList as ViewListIcon,
  Message as MessageIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

// Add local dependencies.
import {
  useGetParentResponsesQuery,
  useGetChecklistMessagesQuery,
  useGetResponseSummaryQuery,
} from '../../../api/classEssentialsApi';
import { MessageCentricView } from './MessageCentricView';
import { ParentCentricView } from './ParentCentricView';

/**
 * Props for ResponsesManagement component
 */
interface ResponsesManagementProps {
  studioId: string;
}

/**
 * Status configuration for response status chips
 */
const STATUS_CONFIG = {
  already_have: {
    label: 'Already Have',
    color: 'default' as const,
    icon: CheckCircleIcon,
  },
  want_to_purchase: {
    label: 'Want to Purchase',
    color: 'info' as const,
    icon: ShoppingCartIcon,
  },
  purchase_online: {
    label: 'Purchase Online',
    color: 'secondary' as const,
    icon: ComputerIcon,
  },
  purchase_in_person: {
    label: 'Purchase from Studio',
    color: 'success' as const,
    icon: StoreIcon,
  },
};

/**
 * Responses Management component.
 * Displays all parent responses to checklist messages with filtering and search.
 *
 * @param props - Component props
 * @returns {React.ReactElement} The responses management component
 */
const ResponsesManagement: React.FC<ResponsesManagementProps> = ({
  studioId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [viewMode, setViewMode] = useState<'list' | 'message' | 'parent'>(
    'message'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [messageFilter, setMessageFilter] = useState<string>('all');

  // Data fetching
  const {
    data: responses = [],
    isLoading: responsesLoading,
    error: responsesError,
  } = useGetParentResponsesQuery(studioId);

  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
  } = useGetResponseSummaryQuery(studioId);

  const { data: messages = [] } = useGetChecklistMessagesQuery(studioId);

  // Filter responses
  const filteredResponses = useMemo(
    () =>
      responses.filter(response => {
        // Search filter (parent email, student name, item name)
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch =
          !searchQuery ||
          response.item_name.toLowerCase().includes(searchLower) ||
          response.notes.toLowerCase().includes(searchLower);

        // Status filter
        const matchesStatus =
          statusFilter === 'all' || response.status === statusFilter;

        // Message filter
        const matchesMessage =
          messageFilter === 'all' || response.message === messageFilter;

        return matchesSearch && matchesStatus && matchesMessage;
      }),
    [responses, searchQuery, statusFilter, messageFilter]
  );

  // Loading state
  if (responsesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (responsesError) {
    return (
      <Alert severity="error">
        Failed to load responses. Please try again.
      </Alert>
    );
  }

  // Empty state
  if (responses.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No Responses Yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Parent responses will appear here once you send checklist messages and
          parents submit their selections.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Create a message in the &quot;Messages&quot; tab to get started!
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* View Toggle */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h6">View Responses</Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
              }
            }}
            size={isMobile ? 'small' : 'medium'}
          >
            <ToggleButton value="message" aria-label="message view">
              <MessageIcon sx={{ mr: { xs: 0, sm: 1 } }} />
              {!isMobile && 'By Message'}
            </ToggleButton>
            <ToggleButton value="parent" aria-label="parent view">
              <PersonIcon sx={{ mr: { xs: 0, sm: 1 } }} />
              {!isMobile && 'By Parent'}
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon sx={{ mr: { xs: 0, sm: 1 } }} />
              {!isMobile && 'List View'}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {/* Summary Views */}
      {(viewMode === 'message' || viewMode === 'parent') && (
        <>
          {summaryLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {!summaryLoading && summaryError && (
            <Alert severity="error">
              Failed to load summary data. Please try again.
            </Alert>
          )}
          {!summaryLoading && !summaryError && viewMode === 'message' && (
            <MessageCentricView
              data={summaryData?.message_summaries || []}
              loading={summaryLoading}
            />
          )}
          {!summaryLoading && !summaryError && viewMode === 'parent' && (
            <ParentCentricView
              data={summaryData?.parent_summaries || []}
              loading={summaryLoading}
            />
          )}
        </>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Filter Controls */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              {/* Search */}
              <TextField
                fullWidth
                placeholder="Search by item name or notes..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 2 }}
              />

              {/* Status Filter */}
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="already_have">Already Have</MenuItem>
                <MenuItem value="want_to_purchase">Want to Purchase</MenuItem>
                <MenuItem value="purchase_online">Purchase Online</MenuItem>
                <MenuItem value="purchase_in_person">
                  Purchase from Studio
                </MenuItem>
              </TextField>

              {/* Message Filter */}
              <TextField
                select
                label="Message"
                value={messageFilter}
                onChange={e => setMessageFilter(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">All Messages</MenuItem>
                {messages.map(message => (
                  <MenuItem key={message.id} value={message.id}>
                    {message.title || `Message ${message.id.substring(0, 8)}`}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Results count */}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Showing {filteredResponses.length} of {responses.length} responses
            </Typography>
          </Paper>

          {/* Responses Table */}
          {filteredResponses.length === 0 ? (
            <Alert severity="info">
              No responses match your filters. Try adjusting your search or
              filter criteria.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item Name</TableCell>
                    {!isMobile && <TableCell>Category</TableCell>}
                    {!isMobile && <TableCell align="right">Price</TableCell>}
                    <TableCell>Status</TableCell>
                    {!isMobile && <TableCell>Notes</TableCell>}
                    {!isMobile && <TableCell>Submitted</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredResponses.map(response => {
                    const statusConfig = STATUS_CONFIG[response.status];
                    const StatusIcon = statusConfig.icon;

                    return (
                      <TableRow key={response.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {response.item_name}
                          </Typography>
                          {isMobile && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {response.item_description}
                            </Typography>
                          )}
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {response.item_description || '—'}
                            </Typography>
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell align="right">
                            {response.item_price
                              ? `$${Number(response.item_price).toFixed(2)}`
                              : '—'}
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            icon={<StatusIcon fontSize="small" />}
                            label={statusConfig.label}
                            color={statusConfig.color}
                            size="small"
                          />
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={response.notes}
                            >
                              {response.notes || '—'}
                            </Typography>
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {new Date(
                                response.created_at
                              ).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default ResponsesManagement;
